'use client';

import type { CodeChangesResponse } from '@/lib/pr-v2/code-changes/types';
import type { PrV2CommentResponse, PrV2CommentType } from '@/lib/pr-v2/types';
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { PrV2ApplySuggestionPayload } from '../../comments/pr-v2-suggestion-block';

import { ensurePrV2FileContent } from '@/data/v2/pr/use-pr-v2-file-content';

type PrV2SuggestionPatchArgs = {
  path: string;
  startLine: number;
  endLine: number;
  suggestion: string;
  originalLines?: string[] | undefined;
};

type ApplyPrV2SuggestionArgs = {
  owner: string;
  repo: string;
  number: string;
  headRef: string | null;
  // Comparison shas for hydrating stripped file contents through the shared per-file cache.
  headSha?: string | null;
  baseSha?: string | null;
  queryClient: QueryClient;
  queryKey?: readonly unknown[] | null;
  comment: PrV2CommentResponse;
  payload: PrV2ApplySuggestionPayload;
  deleteComment: (args: { commentId: number; commentType: PrV2CommentType }) => Promise<void>;
};

const APPLY_SUGGESTION_CLEANUP_ERROR_MESSAGE =
  'Suggestion applied, but failed to remove the original comment';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function asPositiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

function extractCommentLineNumber(comment: PrV2CommentResponse): number | null {
  const raw = asRecord(comment.raw);
  return (
    asPositiveInteger(raw?.line) ??
    asPositiveInteger(comment.line) ??
    asPositiveInteger(raw?.original_line)
  );
}

function extractCommentStartLine(comment: PrV2CommentResponse): number | null {
  const raw = asRecord(comment.raw);
  return asPositiveInteger(raw?.start_line) ?? extractCommentLineNumber(comment);
}

function extractCommentCommitId(comment: PrV2CommentResponse): string | null {
  const raw = asRecord(comment.raw);
  const commitId = raw?.commit_id;
  return typeof commitId === 'string' && commitId.trim().length > 0 ? commitId : null;
}

function parsePositiveInteger(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  if (Number.isInteger(parsed) && parsed > 0) {
    return parsed;
  }

  return null;
}

function splitSuggestionLines(suggestion: string): string[] {
  return suggestion.length === 0 ? [] : suggestion.split('\n');
}

function replaceLines({
  content,
  startLine,
  endLine,
  suggestion,
}: {
  content: string;
  startLine: number;
  endLine: number;
  suggestion: string;
}): string | null {
  const lines = content.split('\n');
  const startIndex = startLine - 1;
  const endIndex = endLine;

  if (startIndex < 0 || endIndex < startLine || endIndex > lines.length) {
    return null;
  }

  const replacementLines = splitSuggestionLines(suggestion);
  return [...lines.slice(0, startIndex), ...replacementLines, ...lines.slice(endIndex)].join('\n');
}

function matchesContiguousRangeAt(
  sourceLines: string[],
  rangeLines: string[],
  startIndex: number,
): boolean {
  if (startIndex < 0 || rangeLines.length === 0) {
    return false;
  }

  if (startIndex + rangeLines.length > sourceLines.length) {
    return false;
  }

  for (let offset = 0; offset < rangeLines.length; offset += 1) {
    if (sourceLines[startIndex + offset] !== rangeLines[offset]) {
      return false;
    }
  }

  return true;
}

function findContiguousRangeMatchStartIndices(
  sourceLines: string[],
  rangeLines: string[],
): number[] {
  if (rangeLines.length === 0 || rangeLines.length > sourceLines.length) {
    return [];
  }

  const matches: number[] = [];
  const lastStartIndex = sourceLines.length - rangeLines.length;
  for (let startIndex = 0; startIndex <= lastStartIndex; startIndex += 1) {
    if (matchesContiguousRangeAt(sourceLines, rangeLines, startIndex)) {
      matches.push(startIndex);
    }
  }

  return matches;
}

function resolveOptimisticSuggestionRange(
  currentLines: string[],
  startLine: number,
  originalLines: string[] | undefined,
): { startLine: number; endLine: number } | null {
  if (!originalLines || originalLines.length === 0) {
    return null;
  }

  const requestedStartIndex = startLine - 1;
  let mappedStartIndex = -1;

  if (matchesContiguousRangeAt(currentLines, originalLines, requestedStartIndex)) {
    mappedStartIndex = requestedStartIndex;
  } else {
    const remappedStartIndices = findContiguousRangeMatchStartIndices(currentLines, originalLines);
    if (remappedStartIndices.length !== 1) {
      return null;
    }

    mappedStartIndex = remappedStartIndices[0]!;
  }

  return {
    startLine: mappedStartIndex + 1,
    endLine: mappedStartIndex + originalLines.length,
  };
}

export function applySuggestionToCodeChanges(
  codeChanges: CodeChangesResponse,
  args: PrV2SuggestionPatchArgs,
): CodeChangesResponse | null {
  const fileIndex = codeChanges.codeContent.findIndex((file) => file.filename === args.path);
  if (fileIndex === -1) {
    return null;
  }

  const file = codeChanges.codeContent[fileIndex];
  if (!file || typeof file.newContent !== 'string') {
    return null;
  }

  const currentLines = file.newContent.split('\n');
  const remappedRange = resolveOptimisticSuggestionRange(
    currentLines,
    args.startLine,
    args.originalLines,
  );
  if (!remappedRange) {
    return null;
  }

  const nextNewContent = replaceLines({
    content: file.newContent,
    startLine: remappedRange.startLine,
    endLine: remappedRange.endLine,
    suggestion: args.suggestion,
  });
  if (nextNewContent == null) {
    return null;
  }

  const nextCodeContent = codeChanges.codeContent.map((entry, index) =>
    index === fileIndex
      ? {
          ...entry,
          newContent: nextNewContent,
          patch: undefined,
        }
      : entry,
  );

  const nextFileDiffEntries = codeChanges.fileDiffEntries?.filter(
    (entry) => entry.filename !== args.path,
  );

  const nextFileDiffByFilename = codeChanges.fileDiffByFilename
    ? Object.fromEntries(
        Object.entries(codeChanges.fileDiffByFilename).filter(
          ([filename]) => filename !== args.path,
        ),
      )
    : codeChanges.fileDiffByFilename;

  return {
    ...codeChanges,
    codeContent: nextCodeContent,
    ...(codeChanges.fileDiffEntries ? { fileDiffEntries: nextFileDiffEntries } : {}),
    ...(codeChanges.fileDiffByFilename ? { fileDiffByFilename: nextFileDiffByFilename } : {}),
  };
}

type HydratedFileContents = {
  oldContent: string | null;
  newContent: string | null;
};

/**
 * Stripped payloads (`contentsOmitted`) carry no `newContent`, which would silently skip the
 * optimistic patch while the server apply still succeeds — leaving a stale diff until the
 * webhook refresh. Fetch the target file's contents from the per-file endpoint (shared cache
 * with the lazy code-view fetch) before computing the optimistic patch. Hydration is
 * best-effort: on failure (`null`) the caller keeps the existing no-op and the server apply
 * proceeds.
 */
async function fetchOmittedFileContents({
  queryClient,
  owner,
  repo,
  number,
  headSha,
  baseSha,
  codeChanges,
  path,
}: {
  queryClient: QueryClient;
  owner: string;
  repo: string;
  number: string;
  headSha?: string | null;
  baseSha?: string | null;
  codeChanges: CodeChangesResponse;
  path: string;
}): Promise<HydratedFileContents | null> {
  const file = codeChanges.codeContent.find((entry) => entry.filename === path);
  if (!file || file.contentsOmitted !== true || typeof file.newContent === 'string' || !file.sha) {
    return null;
  }

  // Hydrating without the resolved comparison SHAs would fetch the wrong file version (the
  // endpoint falls back to canonical summary SHAs), so degrade to the no-op path instead.
  if (!headSha || !baseSha) {
    return null;
  }

  try {
    const fileContents = await ensurePrV2FileContent(queryClient, owner, repo, number, {
      filename: file.filename,
      previousFilename: file.previous_filename ?? null,
      sha: file.sha,
      status: file.status,
      headSha,
      baseSha,
    });

    return {
      oldContent: fileContents.oldContent,
      newContent: fileContents.newContent,
    };
  } catch {
    return null;
  }
}

function mergeHydratedFileContents(
  codeChanges: CodeChangesResponse,
  path: string,
  hydratedContents: HydratedFileContents | null,
): CodeChangesResponse {
  if (!hydratedContents) {
    return codeChanges;
  }

  const fileIndex = codeChanges.codeContent.findIndex((file) => file.filename === path);
  const file = fileIndex === -1 ? undefined : codeChanges.codeContent[fileIndex];
  if (!file || file.contentsOmitted !== true) {
    return codeChanges;
  }

  return {
    ...codeChanges,
    codeContent: codeChanges.codeContent.map((entry, index) =>
      index === fileIndex
        ? {
            ...entry,
            oldContent: entry.oldContent ?? hydratedContents.oldContent,
            newContent: entry.newContent ?? hydratedContents.newContent,
          }
        : entry,
    ),
  };
}

export async function applyPrV2Suggestion({
  owner,
  repo,
  number,
  headRef,
  headSha,
  baseSha,
  queryClient,
  queryKey,
  comment,
  payload,
  deleteComment,
}: ApplyPrV2SuggestionArgs): Promise<void> {
  const commentId = parsePositiveInteger(comment.githubCommentId);
  const lineNumber = extractCommentLineNumber(comment);
  const startLine = extractCommentStartLine(comment);
  const commitId = extractCommentCommitId(comment);
  if (!commentId || !comment.path || !lineNumber || !startLine || !commitId || !headRef) {
    throw new Error('Missing context required to apply suggestion');
  }

  const commentPath = comment.path;

  if (queryKey) {
    await queryClient.cancelQueries({ queryKey, exact: true });
  }

  const snapshotCodeChanges = queryKey
    ? queryClient.getQueryData<CodeChangesResponse>(queryKey)
    : undefined;
  // The network fetch happens before the cache write so the write itself stays synchronous.
  const hydratedContents = snapshotCodeChanges
    ? await fetchOmittedFileContents({
        queryClient,
        owner,
        repo,
        number,
        headSha,
        baseSha,
        codeChanges: snapshotCodeChanges,
        path: commentPath,
      })
    : null;

  // Compute the optimistic patch inside the functional updater against the CURRENT cached
  // value: the hydration await above can interleave with another apply's cache write, and a
  // patch derived from the pre-await snapshot would silently drop that earlier write.
  let previousCodeChanges: CodeChangesResponse | undefined;
  let nextCodeChanges: CodeChangesResponse | null = null;
  if (queryKey) {
    // Capture the value react-query actually STORES (setQueryData's return): structural sharing
    // may clone the updater's return value, and the failure-path compare-and-swap below matches
    // the live cache entry by reference.
    const storedCodeChanges = queryClient.setQueryData<CodeChangesResponse>(
      queryKey,
      (currentCodeChanges) => {
        if (!currentCodeChanges) {
          return undefined;
        }

        const patchedCodeChanges = applySuggestionToCodeChanges(
          mergeHydratedFileContents(currentCodeChanges, commentPath, hydratedContents),
          {
            path: commentPath,
            startLine,
            endLine: lineNumber,
            suggestion: payload.suggestion,
            originalLines: payload.originalLines,
          },
        );
        if (!patchedCodeChanges) {
          // Returning undefined keeps the cached value untouched (hydration-failure no-op).
          return undefined;
        }

        previousCodeChanges = currentCodeChanges;
        return patchedCodeChanges;
      },
    );
    nextCodeChanges = storedCodeChanges ?? null;
  }

  let applySucceeded = false;

  try {
    const response = await fetch(`/api/github/repos/${owner}/${repo}/pulls/${number}/suggestions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        comment_id: commentId,
        commit_id: commitId,
        path: comment.path,
        suggestion: payload.suggestion,
        start_line: startLine,
        end_line: lineNumber,
        headRef,
      }),
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error ?? 'Failed to apply suggestion');
    }

    applySucceeded = true;

    try {
      await deleteComment({
        commentId,
        commentType: comment.commentType === 'issue_comment' ? 'issue_comment' : 'review_comment',
      });
    } catch {
      throw new Error(APPLY_SUGGESTION_CLEANUP_ERROR_MESSAGE);
    }

    toast.success('Suggestion applied');
  } catch (error) {
    if (!applySucceeded && queryKey && nextCodeChanges && previousCodeChanges) {
      const writtenCodeChanges = nextCodeChanges;
      const rollbackCodeChanges = previousCodeChanges;
      // Compare-and-swap rollback: restore the pre-apply snapshot ONLY if this apply's
      // optimistic write is still the current cached value. A later apply may have patched on
      // top of it — unconditionally restoring this snapshot would erase that later successful
      // write.
      let rolledBack = false;
      queryClient.setQueryData<CodeChangesResponse>(queryKey, (currentCodeChanges) => {
        if (currentCodeChanges === writtenCodeChanges) {
          rolledBack = true;
          return rollbackCodeChanges;
        }
        return currentCodeChanges;
      });
      if (!rolledBack) {
        // A later apply patched on top of this one's optimistic write, so the current cache
        // still layers this failed edit underneath it. Refetch so the diff resyncs with the
        // server instead of waiting on a webhook that may be missed or delayed.
        void queryClient.invalidateQueries({ queryKey });
      }
    }

    throw error instanceof Error ? error : new Error('Failed to apply suggestion');
  }
}
